import create from 'zustand';

import {
  getPrimeQuotaDetails,
  getUnlockedEntityStatus,
  postPrimeQuotaConsumption
} from './api';

import { getPrimeQuotaForMode } from './quotaHelpers';

import {
  ConsumeQuotaParams,
  FetchQuotaParams,
  PrimeMode,
  PrimeQuotaResult,
  QuotaDetails,
  QuotaDetailsResponse,
  UserPlatformPlan
} from './types';

interface HydratePrimeQuotaPayload {
  quotaDetails?: QuotaDetails | null;
  userPlatformPlan?: UserPlatformPlan | null;
  unlockedVideosUids?: string[] | null;
}

interface PrimeQuotaStore {
  quotaDetails: QuotaDetails | null;
  userPlatformPlan: UserPlatformPlan | null;
  unlockedVideosUids: string[];

  isFetchingQuotaDetails: boolean;
  isConsumingQuota: boolean;
  isFetchingUnlockedVideos: boolean;

  quotaDetailsError: unknown;
  consumeQuotaError: unknown;
  unlockedVideosError: unknown;

  hydratePrimeQuota: (payload: HydratePrimeQuotaPayload) => void;

  fetchQuotaDetails: (
    params: FetchQuotaParams
  ) => Promise<QuotaDetailsResponse | null>;

  consumeQuota: (params: ConsumeQuotaParams) => Promise<unknown>;

  fetchUnlockedVideosUids: (
    params: FetchQuotaParams
  ) => Promise<string[] | null>;

  getQuotaForMode: (mode: PrimeMode) => PrimeQuotaResult;
  resetPrimeQuotaStore: () => void;
}

const initialState = {
  quotaDetails: null,
  userPlatformPlan: null,
  unlockedVideosUids: [] as string[],

  isFetchingQuotaDetails: false,
  isConsumingQuota: false,
  isFetchingUnlockedVideos: false,

  quotaDetailsError: null,
  consumeQuotaError: null,
  unlockedVideosError: null
};

const usePrimeQuotaStore = create<PrimeQuotaStore>((set, get) => ({
  ...initialState,

  hydratePrimeQuota: (payload) => {
    const currentPlan = get().userPlatformPlan;
    const newPlan = payload.userPlatformPlan;

    if (
      currentPlan === UserPlatformPlan.PAID &&
      newPlan === UserPlatformPlan.FREE
    ) {
      return;
    }
    set({
      quotaDetails: payload.quotaDetails ?? get().quotaDetails,
      userPlatformPlan: payload.userPlatformPlan ?? get().userPlatformPlan,
      unlockedVideosUids: payload.unlockedVideosUids ?? get().unlockedVideosUids
    });
  },

  fetchQuotaDetails: async (params) => {
    // Skip API call if user is not logged in
    if (!params.accessToken) {
      return null;
    }

    try {
      set({
        isFetchingQuotaDetails: true,
        quotaDetailsError: null
      });

      const response = await getPrimeQuotaDetails(params);

      set({
        quotaDetails: response.quotaDetails,
        userPlatformPlan: response.userPlatformPlan
      });

      return response;
    } catch (error) {
      set({
        quotaDetailsError: error
      });

      return null;
    } finally {
      set({
        isFetchingQuotaDetails: false
      });
    }
  },

  consumeQuota: async (params) => {
    // Skip API call if user is not logged in
    if (!params.accessToken) {
      return null;
    }

    try {
      set({
        isConsumingQuota: true,
        consumeQuotaError: null
      });

      const response = await postPrimeQuotaConsumption(params);
      const didConsume =
        response &&
        typeof response === 'object' &&
        'success' in response &&
        Boolean((response as { success?: boolean }).success);

      if (didConsume && params.objectUID) {
        set({
          unlockedVideosUids: Array.from(
            new Set([...get().unlockedVideosUids, params.objectUID])
          )
        });
      }

      /**
       * If API returns fresh quota details, update store directly.
       * Otherwise refetch details.
       */
      if (
        response &&
        typeof response === 'object' &&
        'quotaDetails' in response
      ) {
        const typedResponse = response as {
          quotaDetails?: QuotaDetails;
          userPlatformPlan?: UserPlatformPlan;
        };

        if (typedResponse.quotaDetails) {
          set({
            quotaDetails: typedResponse.quotaDetails,
            userPlatformPlan:
              typedResponse.userPlatformPlan ?? get().userPlatformPlan
          });
        }
      } else {
        await get().fetchQuotaDetails({
          goalUID: params.goalUID,
          accessToken: params.accessToken
        });
      }

      return response;
    } catch (error) {
      set({
        consumeQuotaError: error
      });

      return null;
    } finally {
      set({
        isConsumingQuota: false
      });
    }
  },

  fetchUnlockedVideosUids: async (params) => {
    // Skip API call if user is not logged in
    if (!params.accessToken) {
      return null;
    }

    try {
      set({
        isFetchingUnlockedVideos: true,
        unlockedVideosError: null
      });

      const response = await getUnlockedEntityStatus(params);

      set({
        unlockedVideosUids: Array.from(
          new Set([...get().unlockedVideosUids, ...(response || [])])
        )
      });

      return response;
    } catch (error) {
      set({
        unlockedVideosError: error
      });

      return null;
    } finally {
      set({
        isFetchingUnlockedVideos: false
      });
    }
  },

  getQuotaForMode: (mode) => {
    const { quotaDetails, userPlatformPlan } = get();

    return getPrimeQuotaForMode({
      mode,
      quotaDetails,
      userPlatformPlan
    });
  },

  resetPrimeQuotaStore: () => {
    set(initialState);
  }
}));

export default usePrimeQuotaStore;
